import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, Loader2, QrCode, RefreshCw, Smartphone } from 'lucide-react';
import { createEvolutionInstanceSchema } from '@pms/shared';
import { messageService } from '@/services/message.service';
import { getErrorMessage } from '@/lib/errors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * Baileys rotates the QR roughly once a minute and the stale image simply stops working, so
 * the code is re-read on a timer rather than held open. Comfortably inside the rotation.
 */
const QR_REFRESH_MS = 40_000;

/** How often the pairing poll asks whether the scan has landed. */
const STATE_POLL_MS = 3_000;

type Step = 'name' | 'scan' | 'done';

/**
 * Create-and-pair (or re-pair) a WhatsApp Direct instance.
 *
 * 'create' names a brand-new instance on the Evolution server, shows its QR, and — only
 * after a successful scan — offers to point evolution_config at it. 'repair' skips naming
 * and re-scans the instance already configured, which is the fix for a logged-out phone.
 */
export function EvolutionInstanceDialog({
  open,
  onClose,
  mode,
  instanceName,
}: {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'repair';
  /** The configured instance. Required in 'repair' mode. */
  instanceName?: string;
}) {
  const queryClient = useQueryClient();

  // Only the two steps the user drives. 'done' is derived from the live state below, so a
  // phone that pairs — or drops again — is reflected without a second source of truth.
  const [step, setStep] = useState<'name' | 'scan'>(mode === 'repair' ? 'scan' : 'name');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  /** The instance being paired: typed in 'create' mode, the configured one in 'repair'. */
  const target = mode === 'repair' ? (instanceName ?? '') : name;

  const create = useMutation({
    mutationFn: (value: string) => messageService.createEvolutionInstance(value),
    onSuccess: (pairing) => {
      queryClient.setQueryData(['messages', 'evolution', 'qr', pairing.instance_name], pairing);
      setStep('scan');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const stateQuery = useQuery({
    queryKey: ['messages', 'evolution', 'pair-state', target],
    queryFn: () => messageService.getEvolutionInstanceState(target),
    enabled: open && step === 'scan' && target !== '',
    refetchInterval: STATE_POLL_MS,
    retry: false,
  });

  const state = stateQuery.data?.state ?? null;

  /** What the body renders. The scan step ends the moment the poll reports 'open'. */
  const view: Step = step === 'name' ? 'name' : state === 'open' ? 'done' : 'scan';

  const qrQuery = useQuery({
    queryKey: ['messages', 'evolution', 'qr', target],
    queryFn: () => messageService.getEvolutionInstanceQr(target),
    // Stops the moment pairing lands — a paired instance has no code left to issue.
    enabled: open && view === 'scan' && target !== '',
    // Matches the refresh cadence so the seeded create response is not re-fetched the
    // instant the scan step opens.
    refetchInterval: view === 'scan' ? QR_REFRESH_MS : false,
    retry: false,
  });

  const activate = useMutation({
    mutationFn: () => messageService.updateConfig({ evolution: { instance_name: target } }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['messages', 'config'] }),
        queryClient.invalidateQueries({ queryKey: ['messages', 'evolution', 'status'] }),
      ]);
      toast.success(`WhatsApp Direct now sends from “${target}”`);
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  /** Only ever removes an instance this dialog just created and nobody has adopted yet. */
  const discard = useMutation({
    mutationFn: () => messageService.deleteEvolutionInstance(target),
    onSuccess: () => {
      toast.success('Instance discarded');
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  function submitName() {
    const parsed = createEvolutionInstanceSchema.safeParse({ instance_name: name });
    if (!parsed.success) {
      setNameError(parsed.error.issues[0]?.message ?? 'Invalid name');
      return;
    }
    setNameError(null);
    setName(parsed.data.instance_name);
    create.mutate(parsed.data.instance_name);
  }

  /**
   * A created-but-unpaired instance lingers on the Evolution server, so backing out of
   * 'create' offers to remove it. 'repair' owns nothing and simply closes.
   */
  function requestClose() {
    if (mode === 'create' && view === 'scan') {
      setConfirmingCancel(true);
      return;
    }
    onClose();
  }

  const busy = create.isPending || activate.isPending || discard.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) requestClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'repair' ? 'Re-pair WhatsApp' : 'Add WhatsApp instance'}
          </DialogTitle>
          <DialogDescription>
            {view === 'name'
              ? 'Name the new session on the Evolution server. You will link a phone to it next.'
              : view === 'scan'
                ? 'Open WhatsApp on the phone that will send these messages, then scan the code.'
                : 'The phone is linked and this instance can send.'}
          </DialogDescription>
        </DialogHeader>

        {view === 'name' && (
          <div className="space-y-2">
            <Label htmlFor="evo-new-instance" className="text-xs">Instance name</Label>
            <Input
              id="evo-new-instance"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !create.isPending) submitName(); }}
              placeholder="pms-reminders"
              autoFocus
              disabled={create.isPending}
              aria-invalid={nameError !== null}
            />
            <p className="text-[11px] text-muted-foreground">
              Letters, numbers, hyphen and underscore. It becomes part of every request to the
              server, so pick something you will recognise later.
            </p>
            {nameError && <p className="text-xs text-destructive">{nameError}</p>}
          </div>
        )}

        {view === 'scan' && (
          <div className="space-y-3">
            <div className="flex flex-col items-center gap-3">
              {/*
                Solid white plate, not the translucent card surface: a QR needs a true white
                quiet zone or phone cameras struggle to lock onto it.
              */}
              <div className="flex h-[248px] w-[248px] items-center justify-center rounded-lg border border-border/60 bg-white p-3 shadow-sm">
                {qrQuery.isPending ? (
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                ) : qrQuery.isError ? (
                  <p className="px-3 text-center text-xs text-destructive">
                    {getErrorMessage(qrQuery.error)}
                  </p>
                ) : qrQuery.data?.qr_base64 ? (
                  <img
                    src={qrQuery.data.qr_base64}
                    alt={`WhatsApp pairing code for ${target}`}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <p className="px-3 text-center text-xs text-muted-foreground">
                    No code on offer right now. Refresh to ask for a new one.
                  </p>
                )}
              </div>

              {qrQuery.data?.pairing_code && (
                <p className="text-center text-xs text-muted-foreground">
                  Or enter this code on the phone:{' '}
                  <span className="select-all font-mono text-sm font-semibold text-foreground">
                    {qrQuery.data.pairing_code}
                  </span>
                </p>
              )}

              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-[10px]">
                  {stateQuery.isError ? 'unreachable' : (state ?? 'checking…')}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void qrQuery.refetch()}
                  disabled={qrQuery.isFetching}
                >
                  <RefreshCw className={cn('mr-1 h-3.5 w-3.5', qrQuery.isFetching && 'animate-spin')} />
                  New code
                </Button>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              <Smartphone className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                WhatsApp → <span className="font-medium text-foreground">Linked devices</span> →{' '}
                <span className="font-medium text-foreground">Link a device</span>. The code
                refreshes on its own every 40 seconds until the scan lands.
              </p>
            </div>

            {stateQuery.isError && (
              <p className="text-xs text-destructive">{getErrorMessage(stateQuery.error)}</p>
            )}
          </div>
        )}

        {view === 'done' && (
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
              <Check className="h-6 w-6" />
            </span>
            <p className="text-sm font-medium">“{target}” is linked and online.</p>
            <p className="text-xs text-muted-foreground">
              {mode === 'repair'
                ? 'The nightly sweep can send again.'
                : 'Point WhatsApp Direct at it to start sending from this number.'}
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {confirmingCancel ? (
            <>
              <p className="mr-auto self-center text-xs text-muted-foreground">
                Remove “{target}” from the server?
              </p>
              <Button variant="ghost" size="sm" onClick={() => setConfirmingCancel(false)}>
                Keep it
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => discard.mutate()}
                disabled={discard.isPending}
              >
                {discard.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                Discard
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={requestClose} disabled={busy}>
                {view === 'done' ? 'Close' : 'Cancel'}
              </Button>

              {view === 'name' && (
                <Button size="sm" onClick={submitName} disabled={create.isPending}>
                  {create.isPending
                    ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    : <QrCode className="mr-1 h-3.5 w-3.5" />}
                  Create &amp; show QR
                </Button>
              )}

              {view === 'done' && mode === 'create' && (
                <Button size="sm" onClick={() => activate.mutate()} disabled={activate.isPending}>
                  {activate.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                  Use this instance
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
